import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import {
  CalendarDays, Image, Users, Megaphone, FileText, Newspaper,
  Menu as MenuIcon, HelpCircle, Info, Printer, LogIn, Type,
} from "lucide-react";

/**
 * In-app CMS tutorial. Use `?print=1` (or hit /admin/help/print) to enter
 * print mode which strips the sidebar and expands into a printable one-pager.
 */
export default function AdminHelp() {
  const [params] = useSearchParams();
  const printMode = params.get("print") === "1";

  useEffect(() => {
    if (printMode) {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [printMode]);

  return (
    <div data-testid="admin-help-page" className={printMode ? "print-mode" : ""}>
      <style>{`
        @media print {
          .print-mode nav, .print-mode header, .print-mode aside { display: none !important; }
          .print-mode main { padding: 0 !important; }
          .print-mode { background: white !important; }
          .print-mode a { color: #0b4d8a !important; }
        }
        .print-mode { max-width: 1000px; margin: 0 auto; }
      `}</style>

      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">★ Getting Started</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">How to edit the TIOB website</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            A friendly step-by-step guide for staff and volunteers. Bookmark this page.
          </p>
        </div>
        {!printMode && (
          <a href="/admin/help?print=1" target="_blank" rel="noopener noreferrer"
             className="btn-ghost" data-testid="admin-help-print">
            <Printer className="w-4 h-4" /> Printable cheat sheet
          </a>
        )}
      </div>

      <Section n={1} icon={LogIn} title="Signing in">
        <ol>
          <li>Go to <a href="/admin/login" className="link">templeisraelofbrevard.org/admin/login</a>.</li>
          <li>Enter the email + password we gave you.</li>
          <li>You will land on the <strong>Events</strong> screen. Everything else lives in the sidebar on the left.</li>
          <li>To sign out, click <em>Sign out</em> in the top-right corner.</li>
        </ol>
      </Section>

      <Section n={2} icon={CalendarDays} title="Adding, editing, and deleting a flyer event">
        <p><strong>To add a new event:</strong></p>
        <ol>
          <li>Click <strong>Events</strong> in the sidebar.</li>
          <li>Click the blue <em>New event</em> button (top-right).</li>
          <li>Fill in the title, description, date, time, location, and category.</li>
          <li><strong>Flyer image</strong> - click <em>Pick from library</em> to reuse a flyer you already uploaded, or open <em>Media Library</em> first and upload a new one.</li>
          <li>Paste the ShulCloud RSVP link in <em>RSVP URL</em> (and again in Payment URL if it&rsquo;s the same page).</li>
          <li>Tick <em>Featured</em> to have it show on the home page &ldquo;this week&rdquo; card.</li>
          <li>Click <em>Save event</em>. It appears immediately on <a href="/events" className="link">/events</a>.</li>
        </ol>
        <p><strong>To edit an event:</strong> click the <em>Edit</em> button on that event&rsquo;s row, change what you need, then <em>Save event</em>.</p>
        <p><strong>To delete an event (including past events):</strong></p>
        <ol>
          <li>In the events list, find the row of the event you want to remove.</li>
          <li>Click the red <em>Delete</em> button on the far right of that row.</li>
          <li>Confirm the pop-up. It&rsquo;s gone from the public site immediately.</li>
        </ol>
        <p>You can also delete from inside the edit form: click <em>Edit</em>, then use the red <em>Delete event</em> button in the bottom-right of the save bar.</p>
        <Callout>Deleting is permanent - there&rsquo;s no undo. If you&rsquo;d rather hide an event without removing it, just untick <em>Featured</em> so it drops off the homepage but stays in the calendar for reference.</Callout>
        <Callout>Only events with a flyer show on the public /events page. Everything else (Torah study, weekly Shabbat, mahjong) lives on the Weekly Calendar page - edit it in <em>Weekly Calendar</em> in the sidebar (next section).</Callout>
      </Section>

      <Section n={3} icon={CalendarDays} title="Editing the Weekly Calendar page">
        <p>The <a href="/calendar/weekly" className="link">/calendar/weekly</a> page has three parts you can edit from <strong>Weekly Calendar</strong> in the sidebar:</p>
        <ol>
          <li><strong>Recurring items</strong> - things that happen every week on the same day (Shabbat service, Torah study, mahjong, board meeting).
            <ul>
              <li>Click <em>Add recurring item</em>.</li>
              <li>Pick the weekday (Sun-Sat), fill in title, time, location, description.</li>
              <li>Optional URLs: RSVP, Payment, Details (any external link).</li>
              <li>Tick <em>Featured this week</em> to give it the yellow &ldquo;featured&rdquo; ribbon (only one item per week can be featured - earliest day wins).</li>
              <li>Tick <em>Canceled</em> to show a strike-through &ldquo;canceled&rdquo; label without removing it.</li>
              <li>Use the pause icon on the list to temporarily hide an item without deleting it.</li>
            </ul>
          </li>
          <li><strong>One-off items</strong> - things happening only on a specific date (Rabbi out, building closed, guest speaker).
            <ul>
              <li>Click <em>Add one-off item</em>. Pick the date + fill in the same fields.</li>
              <li>One-off items auto-delete 30 days after their date - no cleanup needed.</li>
            </ul>
          </li>
          <li><strong>Header text</strong> - the eyebrow (small text) and main title above the calendar. The date range and event count below are generated automatically.</li>
        </ol>
        <Callout>Recurring items appear on every week&rsquo;s view. If you want an item to appear only once, use a <em>one-off item</em> instead.</Callout>
      </Section>

      <Section n={4} icon={Image} title="Uploading photos & flyers">
        <ol>
          <li>Click <strong>Media Library</strong> in the sidebar.</li>
          <li>(Optional) Type a friendly label like &ldquo;Passover 2027 flyer.&rdquo;</li>
          <li>Click <em>Upload file</em>. Pick a JPG, PNG, WebP, or PDF up to 10 MB.</li>
          <li>Your file is now in the library. Click <em>Copy URL</em> whenever you need to paste it into another form.</li>
          <li>Click the red trash icon to remove an image (it stops appearing on the site immediately).</li>
        </ol>
      </Section>

      <Section n={5} icon={Users} title="Editing staff, clergy, board & youth leaders">
        <ol>
          <li>Click <strong>Staff & Board</strong>.</li>
          <li>You&rsquo;ll see everyone grouped by section (Clergy, Office, Religious School, Youth, Board).</li>
          <li>Click the pencil to edit a person, the eye to hide/show them, or the trash to remove them.</li>
          <li>Click <em>Add person</em> to add a new board member, staff, or volunteer coordinator.</li>
          <li>Use <em>Display order</em> (smaller shows first) to reorder within a section.</li>
        </ol>
      </Section>

      <Section n={6} icon={Megaphone} title="Posting a site-wide announcement banner">
        <ol>
          <li>Click <strong>Announcements</strong>.</li>
          <li>Click <em>New announcement</em>. Write a short one-line message.</li>
          <li>(Optional) Add a link URL + label if you want the banner to link somewhere (e.g. a service change page).</li>
          <li>Pick a style: blue (info), red (urgent), or gold (celebration).</li>
          <li>Tick <em>Active</em> and hit Save. The banner appears at the top of every page instantly.</li>
          <li>When it&rsquo;s no longer needed, uncheck <em>Active</em> or delete the row.</li>
        </ol>
      </Section>

      <Section n={7} icon={FileText} title="Editing existing page copy">
        <ol>
          <li>Click <strong>Page Copy</strong>.</li>
          <li>Find the block you want to edit - e.g. &ldquo;Homepage - Hero Heading&rdquo; or &ldquo;Message from the President.&rdquo;</li>
          <li>Click <em>Edit</em>. A rich-text editor appears (bold, italic, headings, lists, links).</li>
          <li>Type / paste your new copy. Click <em>Save</em>.</li>
          <li>Refresh the public page to see the change. (Hard refresh with Cmd/Ctrl+Shift+R if it seems cached.)</li>
        </ol>
        <Callout>
          <p style={{margin: 0}}>
            <strong>To change the Shabbat or Torah Study time shown on the home page hero widget:</strong>
          </p>
          <ol style={{marginTop: '0.5rem', marginBottom: 0}}>
            <li>Page Copy → find &ldquo;Home - Shabbat Service Time&rdquo; (key <code>home.shabbat.time</code>).</li>
            <li>Change the text (e.g. &ldquo;Friday &middot; 6:30 PM&rdquo; for a one-off early service).</li>
            <li>Save. The home page updates in seconds. Same idea for &ldquo;Home - Torah Study Time&rdquo;.</li>
          </ol>
        </Callout>
      </Section>

      <Section n={8} icon={Type} title="Renaming a navigation tab">
        <ol>
          <li>Click <strong>Navigation</strong>.</li>
          <li>Find the tab you want (top-level items on the left, dropdown children indented underneath).</li>
          <li>Click the pencil icon.</li>
          <li>Change the <em>Label</em> (e.g. rename &ldquo;Calendar&rdquo; to &ldquo;Schedule&rdquo;) and hit Save.</li>
          <li>Use the up/down arrows to reorder tabs, and the eye icon to hide items without deleting them.</li>
        </ol>
        <Callout>System items can be renamed and reordered, but can&rsquo;t be deleted. Hide them instead if you don&rsquo;t want them visible.</Callout>
      </Section>

      <Section n={9} icon={FileText} title="Creating a brand-new page">
        <ol>
          <li>Click <strong>Pages</strong>.</li>
          <li>Click <em>New page</em>. Give it a title (e.g. &ldquo;Sisterhood Newsletter Archive&rdquo;) - the URL slug is generated automatically.</li>
          <li>(Optional) Add a subtitle, a hero image, and an SEO description.</li>
          <li>Write the page body in the rich-text editor.</li>
          <li>Save. Your page is now live at <code>/pages/&lt;your-slug&gt;</code>.</li>
          <li><strong>Add it to the menu:</strong> go to <em>Navigation</em>, click <em>Add nav item</em>, use the &ldquo;Quick pick a custom page&rdquo; chips to auto-fill the path, choose whether it&rsquo;s top-level or nested under About Us / Worship / etc.</li>
        </ol>
      </Section>

      <Section n={10} icon={Newspaper} title="Publishing a news post">
        <ol>
          <li>Click <strong>News</strong>.</li>
          <li>Click <em>New post</em>. Enter a title and date (defaults to today).</li>
          <li>Add an excerpt (1&ndash;2 sentences shown in the list) and a featured image.</li>
          <li>Write the body - this is a full rich-text editor.</li>
          <li>Tick <em>Published</em> and hit Save. It appears on <a href="/news" className="link">/news</a>.</li>
          <li>To take a post down temporarily, uncheck <em>Published</em> instead of deleting.</li>
        </ol>
      </Section>

      <Section n={11} icon={Users} title="Giving another staff member access">
        <ol>
          <li>Click <strong>Staff Accounts</strong>.</li>
          <li>Click <em>New staff</em>. Enter their email, a temporary password, and their name.</li>
          <li>Share the credentials with them privately. They can rotate the password later (feature coming soon &mdash; until then, delete + recreate).</li>
          <li>Delete an account by clicking the red trash icon.</li>
        </ol>
      </Section>

      <Section n={12} icon={HelpCircle} title="Tips & good habits">
        <ul>
          <li><strong>Save often.</strong> The editor doesn&rsquo;t auto-save.</li>
          <li><strong>Hard refresh</strong> the public site (Cmd/Ctrl + Shift + R) if a change doesn&rsquo;t appear right away.</li>
          <li><strong>Use the Media Library</strong> so you don&rsquo;t re-upload the same flyer twice.</li>
          <li><strong>Keep announcements short</strong> - they&rsquo;re a banner, not a paragraph.</li>
          <li><strong>Ask for help</strong> - Brand Partners can jump in anytime. Email <a href="mailto:hello@bp.agency" className="link">hello@bp.agency</a>.</li>
        </ul>
      </Section>

      {printMode && (
        <div className="mt-10 pt-6 border-t border-[color:var(--line)] text-center text-xs text-[color:var(--muted)]">
          Temple Israel of Brevard - Admin cheat sheet · templeisraelofbrevard.org/admin
        </div>
      )}
    </div>
  );
}

function Section({ n, icon: Icon, title, children }) {
  return (
    <div className="mb-8 rounded-2xl border border-[color:var(--line)] bg-white p-6 md:p-7">
      <h2 className="font-display text-xl md:text-2xl text-[color:var(--blue-ink)] flex items-center gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[color:var(--blue-deep)] text-white text-sm shrink-0">{n}</span>
        <Icon className="w-5 h-5 text-[color:var(--blue-deep)]" />
        {title}
      </h2>
      <div className="prose-tiob text-[color:var(--ink-soft)]">
        {children}
      </div>
    </div>
  );
}

function Callout({ children }) {
  return (
    <div className="mt-4 flex gap-3 p-4 rounded-lg bg-[color:var(--cream)] border-l-4 border-[color:var(--blue-deep)] text-sm">
      <Info className="w-5 h-5 text-[color:var(--blue-deep)] shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}
